import React, { useRef, useState, useEffect } from 'react';
import { 
  isValidMedia, 
  isValidImage, 
  isValidVideo, 
  isValidMediaSize, 
  createMediaPreview, 
  revokeMediaPreview, 
  getMediaType,
  formatFileSize,
  getVideoDuration,
  formatVideoDuration,
  createVideoThumbnail
} from '../utils/mediaUtils';
import '../styles/MediaPicker.css';
import ViewOnceToggle from './ViewOnceToggle';

function MediaPicker({ onMediaSelect, onClose, isVisible }) {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileDetails, setFileDetails] = useState([]); // Store additional file info
  const [viewOnce, setViewOnce] = useState(false);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    const mediaFiles = files.filter(file => isValidMedia(file));
    
    if (mediaFiles.length === 0) {
      alert('Please select valid image or video files');
      return;
    }

    // Check file sizes
    const oversizedFiles = mediaFiles.filter(file => !isValidMediaSize(file));
    if (oversizedFiles.length > 0) {
      const videoFiles = oversizedFiles.filter(isValidVideo);
      const imageFiles = oversizedFiles.filter(isValidImage);
      let message = 'Some files are too large. ';
      
      if (videoFiles.length > 0) {
        message += `Videos must be under 25MB. `;
      }
      if (imageFiles.length > 0) {
        message += `Images must be under 5MB. `;
      }
      message += `${oversizedFiles.length} file(s) will be skipped.`;
      alert(message);
    }

    const validFiles = mediaFiles.filter(file => isValidMediaSize(file));
    
    if (validFiles.length === 0) {
      alert('No valid media files to select. Please choose files within size limits.');
      return;
    }

    // Combine with existing files (if adding more)
    const combinedFiles = [...selectedFiles, ...validFiles];
    const maxFiles = 10; // Limit to 10 files per send
    
    if (combinedFiles.length > maxFiles) {
      alert(`You can only send up to ${maxFiles} files at once. Selecting first ${maxFiles} files.`);
      setSelectedFiles(combinedFiles.slice(0, maxFiles));
    } else {
      setSelectedFiles(combinedFiles);
    }
    
    // Create preview URLs and file details
    setIsLoading(true);
    try {
      const newPreviewsAndDetails = await Promise.all(
        validFiles.slice(0, maxFiles - selectedFiles.length).map(async (file) => {
          const preview = createMediaPreview(file);
          const mediaType = getMediaType(file);
          
          let additionalInfo = {
            type: mediaType,
            size: file.size,
            name: file.name
          };

          if (mediaType === 'video') {
            try {
              const duration = await getVideoDuration(file);
              const thumbnail = await createVideoThumbnail(file);
              additionalInfo.duration = duration;
              additionalInfo.thumbnail = thumbnail;
            } catch (error) {
              console.warn('Could not get video metadata:', error);
              additionalInfo.duration = 0;
              additionalInfo.thumbnail = null;
            }
          }

          return { preview, details: additionalInfo };
        })
      );

      const newPreviews = newPreviewsAndDetails.map(item => item.preview);
      const newDetails = newPreviewsAndDetails.map(item => item.details);
      
      const combinedPreviews = [...previews, ...newPreviews];
      const combinedDetails = [...fileDetails, ...newDetails];
      
      setPreviews(combinedPreviews.slice(0, maxFiles));
      setFileDetails(combinedDetails.slice(0, maxFiles));
      
    } catch (error) {
      console.error('Error processing files:', error);
      alert('Error processing some files. Please try again.');
    } finally {
      setIsLoading(false);
    }
    
    // Reset file input to allow selecting the same files again
    event.target.value = '';
  };

  const handleSendMedia = async () => {
    if (selectedFiles.length > 0) {
      setIsLoading(true);
      try {
        await onMediaSelect(selectedFiles, { viewOnce });
        handleClose();
      } catch (error) {
        console.error('Error sending media:', error);
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    // Clean up preview URLs
    previews.forEach(url => revokeMediaPreview(url));
    setSelectedFiles([]);
    setPreviews([]);
    setFileDetails([]);
    setViewOnce(false);
    onClose();
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    const newDetails = fileDetails.filter((_, i) => i !== index);
    
    // Revoke URL for removed file
    revokeMediaPreview(previews[index]);
    
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
    setFileDetails(newDetails);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isVisible) return null;

  return (
    <div className="media-picker-overlay">
      <div className="media-picker-modal">
        <div className="media-picker-header">
          <h3>Select Media ({selectedFiles.length}/10)</h3>
          <button className="close-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="media-picker-content">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {previews.length === 0 ? (
            <div className="no-media">
              <div className="upload-area" onClick={triggerFileInput}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="9" cy="9" r="2"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
                <p>Click to select images and videos</p>
                <span>Images: JPG, PNG, GIF, WebP (max 5MB)</span>
                <span>Videos: MP4, WebM, MOV, AVI (max 25MB)</span>
                <span>Max 10 files total</span>
              </div>
            </div>
          ) : (
            <div className="media-previews">
              {previews.map((preview, index) => {
                const details = fileDetails[index];
                const isVideo = details?.type === 'video';
                
                return (
                  <div key={index} className="media-preview">
                    <div className="media-container">
                      {isVideo ? (
                        <div className="video-preview">
                          {details?.thumbnail ? (
                            <img src={details.thumbnail} alt={`Video thumbnail ${index + 1}`} />
                          ) : (
                            <video src={preview} />
                          )}
                          <div className="video-overlay">
                            <div className="play-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5,3 19,12 5,21" />
                              </svg>
                            </div>
                            {details?.duration && (
                              <div className="video-duration">
                                {formatVideoDuration(details.duration)}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <img src={preview} alt={`Preview ${index + 1}`} />
                      )}
                    </div>
                    
                    <div className="file-info">
                      <span className="file-type-badge">
                        {isVideo ? 'VIDEO' : 'IMAGE'}
                      </span>
                      <span className="file-size">
                        {formatFileSize(details?.size || 0)}
                      </span>
                    </div>
                    
                    <button 
                      className="remove-file-btn"
                      onClick={() => removeFile(index)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {selectedFiles.length < 10 && (
                <div className="add-more-btn" onClick={triggerFileInput}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  <span>Add More</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="media-picker-footer">
          <ViewOnceToggle enabled={viewOnce} onChange={setViewOnce} disabled={isLoading} />
          <button className="cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button 
            className="send-btn" 
            onClick={handleSendMedia}
            disabled={selectedFiles.length === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="loading-spinner">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Sending...
              </>
            ) : (
              `Send ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MediaPicker;
